import { postConciliation } from "modules/finops/conciliaciones/services/conciliacionService";
import { useState, useEffect } from "react";

export const useTablaZpp = () => {
  // ==================== ESTADOS ====================
  const [conciliaciones, setConciliaciones] = useState([]);
  const [estados, setEstados] = useState([]);
  const [selectedConciliacion, setSelectedConciliacion] = useState("");
  const [selectedEstado, setSelectedEstado] = useState("");
  const [datosCompletos, setDatosCompletos] = useState([]);
  const [datosFiltrados, setDatosFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);

  // ==================== UTILIDADES ====================
  const safeNumber = (num) => {
    const n = parseFloat(num);
    return isNaN(n) ? 0 : Math.round(n); // siempre número entero sin decimales
  };

  // ==================== EFECTOS ====================
  useEffect(() => {
    cargarDatosFiltros();
  }, []);

  // ==================== FUNCIONES DE CARGA ====================
  const cargarDatosFiltros = async () => {
    try {
      setLoading(true);
      const response = await postConciliation("zpp");

      if (response && response.data) {
        setDatosCompletos(response.data);
        setDatosFiltrados(response.data);

        // Conciliaciones únicas
        const llavesUnicas = [...new Set(response.data.map((item) => item.llave_conciliacion))];
        setConciliaciones(llavesUnicas.map((llave, index) => ({ id: index + 1, nombre: llave })));

        // Estados únicos
        const novedadesUnicas = [...new Set(response.data.map((item) => item.novedades_llave))];
        setEstados(novedadesUnicas.map((novedad, index) => ({ id: index + 1, nombre: novedad })));
      }
    } catch (error) {
      console.error("Error al cargar datos de filtros:", error);
    } finally {
      setLoading(false);
    }
  };

  // ==================== PROCESAMIENTO ====================
  const procesarDatosConciliaciones = (data) => {
    const conciliacionesMap = {};

    data.forEach((item) => {
      const llave = item.llave_conciliacion;

      if (!conciliacionesMap[llave]) {
        conciliacionesMap[llave] = {
          llave_conciliacion: llave,
          cantidadTX: 0,
          valorServicio: 0,
          transacciones: [],
          tieneNovedad: false,
        };
      }

      conciliacionesMap[llave].cantidadTX += 1;
      conciliacionesMap[llave].valorServicio += safeNumber(item.valor_servicio);
      conciliacionesMap[llave].transacciones.push(item);

      if (item.novedades_llave !== "SIN NOVEDAD") {
        conciliacionesMap[llave].tieneNovedad = true;
      }
    });

    return Object.values(conciliacionesMap).map((c, index) => ({
      id: index + 1,
      conciliacion: {
        name: c.llave_conciliacion,
        referencia: `TX-${c.cantidadTX}`,
      },
      valorServicio: c.valorServicio,
      estadoConciliacion: c.tieneNovedad ? "Con novedad" : "Sin novedad",
      cantidadTX: c.cantidadTX,
      action: "Ver detalle",
      datosCompletos: c.transacciones,
    }));
  };

  const calcularEstadisticas = (conciliaciones) => {
    let totalPagar = 0;
    let totalTX = 0;
    let conNovedad = 0;
    let sinNovedad = 0;

    conciliaciones.forEach((c) => {
      totalPagar += safeNumber(c.valorServicio);
      totalTX += c.cantidadTX;
      c.estadoConciliacion === "Con novedad" ? conNovedad++ : sinNovedad++;
    });

    return {
      totalPagarComercio: totalPagar,
      totalTransacciones: totalTX,
      conciliacionesConNovedad: conNovedad,
      conciliacionesSinNovedad: sinNovedad,
    };
  };

  const procesarCobroComision = (data) => {
    const comisionMap = {};

    data.forEach((item) => {
      const llave = item.llave_cobro_comision;
      if (!llave) return;

      if (!comisionMap[llave]) {
        comisionMap[llave] = {
          llave_cobro_comision: llave,
          valorServicio: 0,
          comision: 0,
          ivaComision: 0,
          retefuente: 0,
          reteica: 0,
          reteiva: 0,
        };
      }

      comisionMap[llave].valorServicio += safeNumber(item.valor_servicio);
      comisionMap[llave].comision += safeNumber(item.comision);
      comisionMap[llave].ivaComision += safeNumber(item.iva_comision);
      comisionMap[llave].retefuente += safeNumber(item.retefuente);
      comisionMap[llave].reteica += safeNumber(item.reteica);
      comisionMap[llave].reteiva += safeNumber(item.reteiva);
    });

    return Object.values(comisionMap);
  };

  // ==================== FILTROS ====================
  const handleFiltrar = () => {
    let filtrados = [...datosCompletos];
    if (selectedConciliacion) {
      filtrados = filtrados.filter((i) => i.llave_conciliacion === selectedConciliacion);
    }
    if (selectedEstado) {
      filtrados = filtrados.filter((i) => i.novedades_llave === selectedEstado);
    }
    setDatosFiltrados(filtrados);
  };

  const handleLimpiarFiltros = () => {
    setSelectedConciliacion("");
    setSelectedEstado("");
    setDatosFiltrados(datosCompletos);
  };

  // ==================== DATOS TABLAS ====================
  const datosProcesados = procesarDatosConciliaciones(datosFiltrados);
  const stats = calcularEstadisticas(datosProcesados);
  const datosComision = procesarCobroComision(datosCompletos);

  const columns = [
    { Header: "Conciliación", accessor: "conciliacion", width: "40%" },
    { Header: "Cantidad TX", accessor: "cantidadTX", width: "15%" },
    { Header: "Valor Servicio", accessor: "valorServicio", width: "20%" },
    { Header: "Estado", accessor: "estadoConciliacion", width: "20%" },
  ];

  const columnsComision = [
    { Header: "Ciclo Cobro Comisión", accessor: "llave_cobro_comision", width: "30%" },
    { Header: "Valor Transaccionado", accessor: "valorServicio", width: "15%" },
    { Header: "Comisión 2.5%", accessor: "comision", width: "10%" },
    { Header: "IVA 19% Comisión", accessor: "ivaComision", width: "10%" },
    { Header: "Retefuente 3.5%", accessor: "retefuente", width: "10%" },
    { Header: "Reteica 0.96%", accessor: "reteica", width: "10%" },
    { Header: "Reteiva 0.15%", accessor: "reteiva", width: "10%" },
  ];

  const columnsDetalle = [
    { Header: "Acción", accessor: "accion", width: "10%" },
    { Header: "Fecha Transacción", accessor: "fechaTransaccion", width: "15%" },
    { Header: "Fecha Ingreso", accessor: "fechaIngreso", width: "15%" },
    { Header: "Hora Ingreso", accessor: "horaIngreso", width: "10%" },
    { Header: "Fecha Salida", accessor: "fechaSalida", width: "15%" },
    { Header: "Hora Salida", accessor: "horaSalida", width: "10%" },
    { Header: "Tiempo", accessor: "tiempo", width: "8%" },
    { Header: "Placa", accessor: "placa", width: "10%" },
    { Header: "Id Transacción", accessor: "idtransaction", width: "20%" },
    { Header: "Subtotal", accessor: "subtotal", width: "10%" },
    { Header: "Valor Descuento", accessor: "valorDescuento", width: "10%" },
    { Header: "Valor Impuesto", accessor: "valorImpuesto", width: "10%" },
    { Header: "Valor Servicio", accessor: "valorServicio", width: "10%" },
  ];

  const rows = datosProcesados.map((c) => ({
    conciliacion: {
      name: c.conciliacion.name,
      referencia: c.conciliacion.referencia,
      estado: c.estadoConciliacion,
    },
    cantidadTX: c.cantidadTX,
    valorServicio: `${safeNumber(c.valorServicio).toLocaleString("es-CO")}`,
    estadoConciliacion: c.estadoConciliacion,
  }));

  const rowsComision = datosComision.map((item) => ({
    llave_cobro_comision: item.llave_cobro_comision,
    valorServicio: `${safeNumber(item.valorServicio).toLocaleString("es-CO")}`,
    comision: `${safeNumber(item.comision).toLocaleString("es-CO")}`,
    ivaComision: `${safeNumber(item.ivaComision).toLocaleString("es-CO")}`,
    retefuente: `${safeNumber(item.retefuente).toLocaleString("es-CO")}`,
    reteica: `${safeNumber(item.reteica).toLocaleString("es-CO")}`,
    reteiva: `${safeNumber(item.reteiva).toLocaleString("es-CO")}`,
  }));

  const rowsDetalle = datosFiltrados.map((item) => {
    const accion = item.description ? item.description.split(":")[0] : "-";
    const horaSalida = item.hora_salida
      ? item.hora_salida
      : item.fecha_salida
      ? new Date(item.fecha_salida).toLocaleTimeString("es-CO", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
      : "-";

    return {
      accion,
      fechaTransaccion: item.fecha_transaccion || "-",
      fechaIngreso: item.fecha_ingreso || "-",
      horaIngreso: item.hora_ingreso || "-",
      fechaSalida: item.fecha_salida
        ? new Date(item.fecha_salida).toLocaleDateString("es-CO")
        : "-",
      horaSalida,
      tiempo: item.tiempo_parqueo || "-",
      placa: item.placa || "-",
      idtransaction: item.idtransaction || "-",
      subtotal: `$${safeNumber(item.subtotal).toLocaleString("es-CO")}`,
      valorDescuento: `$${safeNumber(item.valor_descuento).toLocaleString("es-CO")}`,
      valorImpuesto: `$${safeNumber(item.valor_impuesto).toLocaleString("es-CO")}`,
      valorServicio: `$${safeNumber(item.valor_servicio).toLocaleString("es-CO")}`,
    };
  });

  // ==================== RETORNO ====================
  return {
    conciliaciones,
    estados,
    selectedConciliacion,
    setSelectedConciliacion,
    selectedEstado,
    setSelectedEstado,
    loading,
    stats,
    columns,
    rows,
    columnsComision,
    rowsComision,
    columnsDetalle,
    rowsDetalle,
    handleFiltrar,
    handleLimpiarFiltros,
    cargarDatosFiltros,
  };
};
